import React, { useCallback, useEffect } from 'react'
import { Button } from 'antd'
import { LogoutOutlined, PoweroffOutlined, ReloadOutlined } from '@ant-design/icons'
import { useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import { Helmet } from 'react-helmet'
import { setCatalogSyncedEndpoint, setPosZeroLoading } from '@/redux/cashierSlice'
import { getActiveEndpoint } from '@/utils/posLocalDb/queueContext'
import CatalogSyncProgress from './CatalogSyncProgress'
import useCatalogSyncRunner from './useCatalogSyncRunner'
import styles from './style.module.scss'

/**
 * Gate sinkronisasi katalog (Mode Zero-Loading): tampil sebelum kasir bila katalog
 * lokal tenant aktif belum pernah tersinkron (pertama kali / ganti perusahaan).
 * Sukses → simpan endpoint tersinkron → gate lolos.
 */
const CatalogSyncGate = ({ onLogout }) => {
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const t = (id) => formatMessage({ id })

  const handleSuccess = useCallback(
    (endpoint) => {
      dispatch(setCatalogSyncedEndpoint(endpoint))
    },
    [dispatch],
  )

  const runner = useCatalogSyncRunner({ onSuccess: handleSuccess })
  const { run } = runner

  useEffect(() => {
    run()
  }, [run])

  // Lanjut ke kasir walau langkah promo gagal (produk sudah tersinkron).
  const handleSkip = useCallback(() => {
    dispatch(setCatalogSyncedEndpoint(getActiveEndpoint()))
  }, [dispatch])

  // Kembali ke mode biasa (semua data langsung dari server).
  const handleDisable = useCallback(() => {
    dispatch(setPosZeroLoading(false))
  }, [dispatch])

  const isError = runner.status === 'error'

  return (
    <>
      <Helmet title={t('pos.catalog_sync.title')} />
      <div className={styles.gate}>
        <img
          src="resources/images/login-bg-decor.svg?v=3"
          alt=""
          aria-hidden="true"
          className={styles.bgDecor}
        />
        <div className={styles.gateCard}>
          <CatalogSyncProgress
            status={runner.status}
            step={runner.step}
            errorType={runner.errorType}
            errorDetail={runner.errorDetail}
          />
          {isError && (
            <div className={styles.actions}>
              <Button type="primary" size="large" icon={<ReloadOutlined />} onClick={run} block>
                {t('pos.catalog_sync.retry')}
              </Button>
              {runner.canSkip && (
                <Button size="large" onClick={handleSkip} block>
                  {t('pos.catalog_sync.skip')}
                </Button>
              )}
              <Button size="large" icon={<PoweroffOutlined />} onClick={handleDisable} block>
                {t('pos.catalog_sync.disable')}
              </Button>
              {onLogout && (
                <Button type="link" icon={<LogoutOutlined />} onClick={onLogout}>
                  Logout
                </Button>
              )}
            </div>
          )}
        </div>
        <div className={styles.footer}>
          Powered by <strong>Kledo</strong> · ©{` ${new Date().getFullYear()} Kledo`}
        </div>
      </div>
    </>
  )
}

export default CatalogSyncGate
